import type { ColumnState } from 'ag-grid-community'

type StoredColumnState = {
  version: 1
  contexts: Record<string, ColumnState[]>
}

const parseStoredColumnState = (value: string | null): unknown => {
  if (!value) return null

  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

const isColumnStateArray = (value: unknown): value is ColumnState[] =>
  Array.isArray(value) &&
  value.every(
    item =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as ColumnState).colId === 'string'
  )

const hasMatchingColumnIds = (
  columnState: ColumnState[],
  currentColumnIds: string[]
): boolean => {
  if (columnState.length !== currentColumnIds.length) return false

  const savedColumnIds = new Set(columnState.map(({ colId }) => colId))
  return (
    savedColumnIds.size === currentColumnIds.length &&
    currentColumnIds.every(columnId => savedColumnIds.has(columnId))
  )
}

/**
 * Read widths for the exact table structure. Legacy array values are accepted
 * only when their column ids exactly match the current grid, preventing stale
 * state from suppressing autosizing after attributes are added or removed.
 */
export const getColumnStateForContext = (
  storedValue: string | null,
  contextKey: string,
  currentColumnIds: string[]
): ColumnState[] | null => {
  const parsedValue = parseStoredColumnState(storedValue)

  if (isColumnStateArray(parsedValue)) {
    return hasMatchingColumnIds(parsedValue, currentColumnIds)
      ? parsedValue
      : null
  }

  if (
    typeof parsedValue !== 'object' ||
    parsedValue === null ||
    (parsedValue as StoredColumnState).version !== 1
  ) {
    return null
  }

  const contextState = (parsedValue as StoredColumnState).contexts?.[contextKey]
  return isColumnStateArray(contextState) &&
    hasMatchingColumnIds(contextState, currentColumnIds)
    ? contextState
    : null
}

/** Preserve widths independently for each flat/grouped column structure. */
export const serializeColumnStateForContext = (
  storedValue: string | null,
  contextKey: string,
  columnState: ColumnState[]
): string => {
  const parsedValue = parseStoredColumnState(storedValue)
  const existingContexts =
    typeof parsedValue === 'object' &&
    parsedValue !== null &&
    (parsedValue as StoredColumnState).version === 1 &&
    typeof (parsedValue as StoredColumnState).contexts === 'object'
      ? (parsedValue as StoredColumnState).contexts
      : {}

  return JSON.stringify({
    version: 1,
    contexts: {
      ...existingContexts,
      [contextKey]: columnState,
    },
  } satisfies StoredColumnState)
}
